/**
 * Broadcast link API (EPIC-108, wave B1).
 *
 * Two kinds of callers:
 *  - authenticated admin/analyst helpers (broadcasters, sessions) go through
 *    the shared `apiClient` (Firebase ID-token interceptor);
 *  - the token-authenticated broadcast and control pages carry their own
 *    credential in `Authorization: Broadcast <token>` /
 *    `Authorization: BroadcastControl <token>`. Those use a bare axios call
 *    (the apiClient interceptor would overwrite the header) and the token is
 *    NEVER placed in a path, query string, or log line.
 */
import axios from "axios";
import type { AnimSpec } from "../pages/broadcast/animation/enterExit";
import type { PanelConfig } from "../pages/broadcast/panel/Panel";
import { apiClient } from "./api";
import { LONG_TIMEOUT_MS } from "./httpTimeouts";

const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : "http://localhost:8000/api/v1";

export type BroadcastAspect = "16:9" | "16:10";
export type BroadcastBackground =
  | "transparent"
  | "solid"
  | "branded"
  | "chroma"
  | "image";
export type BroadcastWidgetType =
  | "scoreboard"
  | "live_pitch"
  | "callouts"
  | "stats_panel"
  | "heat_map"
  | "area_control"
  | "shot_map"
  | "top_players"
  | "image";
export type CalloutKind =
  | "goal"
  | "penalty"
  | "yellow_card"
  | "red_card"
  | "foul";

export const CALLOUT_KINDS: readonly CalloutKind[] = [
  "goal",
  "penalty",
  "yellow_card",
  "red_card",
  "foul",
];

export interface BroadcastStage {
  aspect: BroadcastAspect;
  background: BroadcastBackground;
  background_color: string;
  /** `background: "image"`: a library `background` asset (null = none). */
  background_asset_id?: string | null;
  global_text_scale: number;
  data_delay_ms: number;
}

export interface BroadcastWidget {
  id: string;
  type: BroadcastWidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  opacity: number;
  text_scale: number;
  visible: boolean;
  options: Record<string, unknown>;
  /** EPIC-109 W3, all null/absent = the legacy look. */
  panel?: PanelConfig | null;
  enter?: AnimSpec | null;
  exit?: AnimSpec | null;
}

export interface CalloutSetting {
  enabled: boolean;
  duration_ms: number;
}

export interface BroadcastLayout {
  version: number;
  stage: BroadcastStage;
  widgets: BroadcastWidget[];
  callouts: Record<CalloutKind, CalloutSetting>;
}

export interface Broadcaster {
  broadcaster_id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  created_at?: string;
}

export interface BroadcasterInput {
  name: string;
  primary_color: string;
  secondary_color: string;
}

export interface BroadcastSession {
  session_id: string;
  match_id: string;
  broadcaster_id: string;
  broadcaster_name: string;
  broadcast_token: string;
  enabled: boolean;
  revoked_at: string | null;
  created_at: string;
  /** Present ONLY in the create / regenerate responses. */
  control_token?: string;
  /** EPIC-109 on-air state. Absent on a pre-EPIC-109 backend. */
  on_air?: boolean;
  on_air_since?: string | null;
  auto_off_at?: string | null;
  expires_at?: string | null;
  state?: BroadcastSessionState;
  screens_connected?: number;
}

export type BroadcastSessionState = "on_air" | "off" | "expired" | "revoked";

export type SceneTransitionKind =
  | "cut"
  | "fade"
  | "slide-left"
  | "slide-right"
  | "slide-up"
  | "zoom";

export interface SceneTransition {
  type: SceneTransitionKind;
  duration_ms: number;
}

export interface BroadcastScene {
  scene_id: string;
  name: string;
  aspect: "16:9" | "16:10";
  layout: BroadcastLayout;
}

export const MAX_SCENES = 12;

export interface BroadcastState {
  /** W3: asset_id -> url for every asset the returned layout(s) reference. */
  assets?: Record<string, string>;
  /** EPIC-109 W2 (control state only). */
  scenes?: BroadcastScene[];
  program_scene_id?: string;
  preview_scene_id?: string;
  default_transition?: SceneTransition;
  /** Session-wide monotonic counter that orders layout messages. */
  layout_seq?: number;
  /** EPIC-109. `false` means the link is Off: no layout / events follow. */
  on_air?: boolean;
  server_time: string;
  session: { session_id: string; enabled: boolean };
  layout: BroadcastLayout;
  broadcaster: {
    name: string;
    logo_url: string | null;
    primary_color: string;
    secondary_color: string;
  };
  match: Record<string, any>;
  events: Record<string, any>[];
  /** Control state only. */
  broadcast_url_token?: string;
  state?: BroadcastSessionState;
  auto_off_at?: string | null;
  expires_at?: string | null;
}

// ---------------------------------------------------------------------------
// Authenticated (admin / analyst)
// ---------------------------------------------------------------------------

export const listBroadcasters = () =>
  apiClient.get<Broadcaster[]>("/broadcasters");

export const createBroadcaster = (body: BroadcasterInput) =>
  apiClient.post<Broadcaster>("/broadcasters", body);

export const updateBroadcaster = (
  id: string,
  body: Partial<BroadcasterInput>,
) =>
  apiClient.patch<Broadcaster>(`/broadcasters/${encodeURIComponent(id)}`, body);

export const deleteBroadcaster = (id: string) =>
  apiClient.delete<unknown>(`/broadcasters/${encodeURIComponent(id)}`);

export const uploadBroadcasterLogo = (id: string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return apiClient.post<Broadcaster>(
    `/broadcasters/${encodeURIComponent(id)}/logo`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

export const listBroadcastSessions = (matchId: string) =>
  apiClient.get<BroadcastSession[]>(
    `/matches/${encodeURIComponent(matchId)}/broadcast-sessions`,
  );

export const createBroadcastSession = (
  matchId: string,
  broadcasterId: string,
  onAir = false,
) =>
  apiClient.post<BroadcastSession>(
    `/matches/${encodeURIComponent(matchId)}/broadcast-sessions`,
    { broadcaster_id: broadcasterId, on_air: onAir },
  );

export const setBroadcastSessionOnAir = (sessionId: string, onAir: boolean) =>
  apiClient.patch<BroadcastSession>(
    `/broadcast-sessions/${encodeURIComponent(sessionId)}`,
    { on_air: onAir },
  );

export const reenableBroadcastSession = (sessionId: string) =>
  apiClient.post<BroadcastSession>(
    `/broadcast-sessions/${encodeURIComponent(sessionId)}/reenable`,
    {},
  );

export const regenerateBroadcastSession = (
  sessionId: string,
  which: "broadcast" | "control" | "both",
) =>
  apiClient.post<BroadcastSession>(
    `/broadcast-sessions/${encodeURIComponent(sessionId)}/regenerate`,
    { which },
  );

export const revokeBroadcastSession = (sessionId: string) =>
  apiClient.post<BroadcastSession>(
    `/broadcast-sessions/${encodeURIComponent(sessionId)}/revoke`,
    {},
  );

export const setBroadcastSessionEnabled = (
  sessionId: string,
  enabled: boolean,
) =>
  apiClient.patch<BroadcastSession>(
    `/broadcast-sessions/${encodeURIComponent(sessionId)}`,
    { enabled },
  );

// ---------------------------------------------------------------------------
// Token-authenticated (broadcast page / control page)
// ---------------------------------------------------------------------------

const tokenHeaders = (
  scheme: "Broadcast" | "BroadcastControl",
  token: string,
) => ({
  Authorization: `${scheme} ${token}`,
});

export const fetchBroadcastState = async (
  token: string,
): Promise<BroadcastState> => {
  const resp = await axios.get<BroadcastState>(
    `${API_BASE_URL}/broadcast/state`,
    {
      headers: tokenHeaders("Broadcast", token),
      timeout: LONG_TIMEOUT_MS,
    },
  );
  return resp.data;
};

/** `GET /broadcast/status`: tiny On/Off probe the Off page polls. */
export const fetchBroadcastStatus = async (
  token: string,
): Promise<{ on_air: boolean }> => {
  const resp = await axios.get<{ on_air: boolean }>(
    `${API_BASE_URL}/broadcast/status`,
    {
      headers: tokenHeaders("Broadcast", token),
      timeout: LONG_TIMEOUT_MS,
    },
  );
  return resp.data;
};

/** `PUT /broadcast/control/on-air`: the producer's On air / Off switch. */
export const putControlOnAir = async (
  token: string,
  onAir: boolean,
): Promise<{ on_air: boolean; state: BroadcastSessionState }> => {
  const resp = await axios.put<{
    on_air: boolean;
    state: BroadcastSessionState;
  }>(
    `${API_BASE_URL}/broadcast/control/on-air`,
    { on_air: onAir },
    {
      headers: tokenHeaders("BroadcastControl", token),
      timeout: LONG_TIMEOUT_MS,
    },
  );
  return resp.data;
};

/** A 4xx other than timeout / rate limit means the link is gone. */
export const isLinkGoneError = (err: unknown): boolean => {
  const code = (err as { response?: { status?: number } })?.response?.status;
  return (
    typeof code === "number" &&
    code >= 400 &&
    code < 500 &&
    code !== 408 &&
    code !== 429
  );
};

export const fetchControlState = async (
  token: string,
): Promise<BroadcastState> => {
  const resp = await axios.get<BroadcastState>(
    `${API_BASE_URL}/broadcast/control/state`,
    {
      headers: tokenHeaders("BroadcastControl", token),
      timeout: LONG_TIMEOUT_MS,
    },
  );
  return resp.data;
};

export const putControlLayout = async (
  token: string,
  layout: BroadcastLayout,
): Promise<BroadcastLayout> => {
  const resp = await axios.put<{ layout: BroadcastLayout }>(
    `${API_BASE_URL}/broadcast/control/layout`,
    { layout },
    {
      headers: tokenHeaders("BroadcastControl", token),
      timeout: LONG_TIMEOUT_MS,
    },
  );
  return resp.data.layout;
};

const controlHeaders = (token: string) => ({
  headers: tokenHeaders("BroadcastControl", token),
  timeout: LONG_TIMEOUT_MS,
});
const sceneUrl = (id: string) =>
  `${API_BASE_URL}/broadcast/control/scenes/${encodeURIComponent(id)}`;

export interface SceneWrite {
  scene: BroadcastScene;
  layout_seq: number;
}

export const createScene = async (
  token: string,
  body: { name: string; aspect?: "16:9" | "16:10"; from_scene_id?: string },
): Promise<SceneWrite> =>
  (
    await axios.post<SceneWrite>(
      `${API_BASE_URL}/broadcast/control/scenes`,
      body,
      controlHeaders(token),
    )
  ).data;

export const patchScene = async (
  token: string,
  sceneId: string,
  body: { name?: string; aspect?: "16:9" | "16:10"; layout?: BroadcastLayout },
): Promise<SceneWrite> =>
  (
    await axios.patch<SceneWrite>(
      sceneUrl(sceneId),
      body,
      controlHeaders(token),
    )
  ).data;

export const duplicateScene = async (
  token: string,
  sceneId: string,
): Promise<SceneWrite> =>
  (
    await axios.post<SceneWrite>(
      `${sceneUrl(sceneId)}/duplicate`,
      {},
      controlHeaders(token),
    )
  ).data;

export const deleteScene = async (
  token: string,
  sceneId: string,
): Promise<void> => {
  await axios.delete(sceneUrl(sceneId), controlHeaders(token));
};

export const putSceneOrder = async (
  token: string,
  sceneIds: string[],
): Promise<void> => {
  await axios.put(
    `${API_BASE_URL}/broadcast/control/scenes/order`,
    { scene_ids: sceneIds },
    controlHeaders(token),
  );
};

export const putPreviewScene = async (
  token: string,
  sceneId: string,
): Promise<void> => {
  await axios.put(
    `${API_BASE_URL}/broadcast/control/preview`,
    { scene_id: sceneId },
    controlHeaders(token),
  );
};

export const putDefaultTransition = async (
  token: string,
  transition: SceneTransition,
): Promise<void> => {
  await axios.put(
    `${API_BASE_URL}/broadcast/control/default-transition`,
    transition,
    controlHeaders(token),
  );
};

export const postTake = async (
  token: string,
  sceneId: string,
  transition?: SceneTransition,
): Promise<{
  program_scene_id: string;
  layout_seq: number;
  transition: SceneTransition;
}> =>
  (
    await axios.post(
      `${API_BASE_URL}/broadcast/control/take`,
      transition ? { scene_id: sceneId, transition } : { scene_id: sceneId },
      controlHeaders(token),
    )
  ).data;

export const postControlCallout = async (
  token: string,
  action: "dismiss" | "replay",
): Promise<void> => {
  await axios.post(
    `${API_BASE_URL}/broadcast/control/callout`,
    { action },
    {
      headers: tokenHeaders("BroadcastControl", token),
      timeout: LONG_TIMEOUT_MS,
    },
  );
};

export interface BroadcastPreset {
  preset_id: string;
  broadcaster_id: string;
  name: string;
  layout: BroadcastLayout;
  created_at?: string;
  updated_at?: string;
}

export const listPresets = async (token: string): Promise<BroadcastPreset[]> =>
  (
    await axios.get<BroadcastPreset[]>(
      `${API_BASE_URL}/broadcast/control/presets`,
      controlHeaders(token),
    )
  ).data;

export const createPreset = async (
  token: string,
  name: string,
  layout: BroadcastLayout,
): Promise<BroadcastPreset> =>
  (
    await axios.post<BroadcastPreset>(
      `${API_BASE_URL}/broadcast/control/presets`,
      { name, layout },
      controlHeaders(token),
    )
  ).data;

export const renamePreset = async (
  token: string,
  presetId: string,
  name: string,
): Promise<BroadcastPreset> =>
  (
    await axios.patch<BroadcastPreset>(
      `${API_BASE_URL}/broadcast/control/presets/${encodeURIComponent(presetId)}`,
      { name },
      controlHeaders(token),
    )
  ).data;

export const deletePreset = async (
  token: string,
  presetId: string,
): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/broadcast/control/presets/${encodeURIComponent(presetId)}`,
    controlHeaders(token),
  );
};

/** Readable message for a 422 from PUT layout (FastAPI `detail`). */
export const describeLayoutError = (err: unknown): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) =>
        typeof d === "string" ? d : String((d as { msg?: string })?.msg ?? ""),
      )
      .filter(Boolean)
      .join("; ");
  }
  return "";
};

export const resolveBroadcastWsBaseUrl = (): string => {
  const explicit = import.meta.env.VITE_WS_URL;
  if (explicit) return explicit.replace(/\/+$/, "");
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    const withoutApi = apiUrl.replace(/\/+$/, "").replace(/\/api\/v1$/, "");
    if (withoutApi.startsWith("https://"))
      return withoutApi.replace("https://", "wss://");
    if (withoutApi.startsWith("http://"))
      return withoutApi.replace("http://", "ws://");
  }
  if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }
  return "ws://localhost:8000";
};

export const buildBroadcastUrl = (origin: string, token: string): string =>
  `${origin}/broadcast/${token}`;
export const buildControlUrl = (origin: string, token: string): string =>
  `${origin}/broadcast/control/${token}`;

// ---------------------------------------------------------------------------
// Image library (EPIC-109 W3, control token)
// ---------------------------------------------------------------------------

export type AssetKind = "logo" | "background";

export interface BroadcastAsset {
  asset_id: string;
  kind: AssetKind;
  url: string;
  width: number;
  height: number;
  format: "png" | "jpeg";
  bytes: number;
  created_at: string;
}

export interface AssetUsage {
  session_id: string;
  scene_id?: string;
  preset_id?: string;
  on_air_program: boolean;
}

export const listAssets = async (token: string): Promise<BroadcastAsset[]> =>
  (
    await axios.get<BroadcastAsset[]>(
      `${API_BASE_URL}/broadcast/control/assets`,
      controlHeaders(token),
    )
  ).data;

export const uploadAsset = async (
  token: string,
  kind: AssetKind,
  file: File,
): Promise<BroadcastAsset> => {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file);
  return (
    await axios.post<BroadcastAsset>(
      `${API_BASE_URL}/broadcast/control/assets`,
      form,
      controlHeaders(token),
    )
  ).data;
};

export const deleteAsset = async (
  token: string,
  assetId: string,
  force = false,
): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/broadcast/control/assets/${encodeURIComponent(assetId)}${
      force ? "?force=true" : ""
    }`,
    controlHeaders(token),
  );
};

/** Usages from a 409 delete response (`{detail, usages}` at the top level). */
export const assetUsagesFromError = (err: unknown): AssetUsage[] | null => {
  const r = (err as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (r?.status !== 409) return null;
  const usages = (r.data as { usages?: unknown })?.usages;
  return Array.isArray(usages) ? (usages as AssetUsage[]) : null;
};

/** Readable text for an upload failure (413 / 422 / 409 / 429). */
export const describeAssetError = (
  err: unknown,
): { status: number | null; detail: string } => {
  const status =
    (err as { response?: { status?: number } })?.response?.status ?? null;
  return { status, detail: describeLayoutError(err) };
};
